/**
 * v-reveal: fade + slide-in when the element scrolls into view.
 * Respects prefers-reduced-motion (revealed instantly).
 * Optional value = delay in ms, e.g. v-reveal="120".
 */
export default defineNuxtPlugin((nuxtApp) => {
  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

  let observer: IntersectionObserver | null = null

  const ensureObserver = () => {
    if (observer || typeof IntersectionObserver === 'undefined') return observer

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible')
            observer?.unobserve(entry.target)
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    )

    return observer
  }

  nuxtApp.vueApp.directive('reveal', {
    mounted(el: HTMLElement, binding) {
      if (binding.value === false) return

      if (prefersReducedMotion()) {
        el.classList.add('reveal-visible')
        return
      }

      // Already in view at hydration (e.g. above the fold): show without
      // hiding first, to avoid a flash from the SSR-rendered content.
      const rect = el.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      if (rect.top < viewportHeight * 0.9 && rect.bottom > 0) {
        el.classList.add('reveal', 'reveal-visible')
        return
      }

      const delay = Number(binding.value)
      if (Number.isFinite(delay) && delay > 0) {
        el.style.transitionDelay = `${delay}ms`
      }

      el.classList.add('reveal')

      const obs = ensureObserver()
      if (obs) {
        obs.observe(el)
      } else {
        el.classList.add('reveal-visible')
      }
    },
    unmounted(el: HTMLElement) {
      observer?.unobserve(el)
    },
  })
})
